import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Avatar } from "@/src/common/components/ui/avatar";
import { env } from "@/src/common/constants/env";
import { clsx } from "@/src/common/utils/clsx";

interface SidebarProps {
	children: React.ReactNode;
	/** Optional current user descriptor for the footer chip. */
	user?: {
		name: string;
		role: string;
		initials?: string;
		avatarUrl?: string | null;
	};
	/** When true, render the sidebar as an off-canvas drawer (mobile). */
	isOpen?: boolean;
	onClose?: () => void;
	className?: string;
}

/**
 * Authed app sidebar — 272px wide, white surface, 1px right border.
 * Header = app brand mark.
 * Middle = sections passed as children (use SidebarSection + SidebarItem).
 * Footer = read-only current-user chip. Sign-out lives in the TopBar
 * dropdown.
 */
export function Sidebar({ children, user, isOpen = false, onClose, className }: SidebarProps) {
	const { t } = useTranslation("common");
	return (
		<>
			{isOpen ? (
				<button
					type="button"
					aria-label={t("aria.close_menu")}
					onClick={onClose}
					className="fixed inset-0 z-30 bg-foreground/40 md:hidden"
				/>
			) : null}
			<aside
				className={clsx(
					"flex h-screen w-[272px] shrink-0 flex-col gap-4 border-r border-border bg-background p-4",
					"fixed inset-y-0 left-0 z-40 -translate-x-full transition-transform duration-200 md:static md:translate-x-0",
					isOpen ? "translate-x-0" : null,
					className,
				)}
			>
				<header className="flex items-center px-2 py-2">
					<Link to="/" className="flex items-center gap-2.5">
						<img src="/logo-banner.png" alt={env.APP_NAME} className="h-10" />
					</Link>
				</header>
				<nav className="flex flex-1 flex-col gap-2 overflow-y-auto">{children}</nav>
				{user ? (
					<div className="flex items-center gap-3 rounded-s bg-surface px-3 py-3">
						<Avatar size="md" name={user.name} initials={user.initials} src={user.avatarUrl ?? undefined} />
						<div className="flex flex-1 flex-col gap-0.5">
							<p className="truncate text-sm font-semibold text-foreground">{user.name}</p>
							<p className="truncate text-[11px] text-muted-foreground">{user.role}</p>
						</div>
					</div>
				) : null}
			</aside>
		</>
	);
}
